import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCatalogoExamesConsultaControllerListar } from '@/api/generated/catalogo-exames/catalogo-exames';
import {
  getExamesControllerListarQueryKey,
  useExamesControllerAtualizar,
  useExamesControllerCriar,
} from '@/api/generated/exames/exames';
import type { ExameResponse, ExameResponseStatus } from '@/api/generated/model';
import { usePacientesControllerListar } from '@/api/generated/pacientes/pacientes';
import { aplicarErrosDoServidor } from '@/api/erros';
import {
  campoDeHorasAposMedicacao,
  DICA_HORAS_APOS_MEDICACAO,
  horasParaEnvio,
} from '@/features/exames/horasAposMedicacao';
import { dataParaISO, isoParaData, ROTULO_STATUS, STATUS } from '@/features/exames/rotulos';
import { numeroParaCampo } from '@/features/historico/tratamento';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';

const schema = z.object({
  pacienteId: z.string().min(1, 'Escolha o paciente'),
  catalogoExameId: z.string().min(1, 'Escolha o exame'),
  status: z.string(),
  os: z.string().trim().max(60),
  coletadoEm: z.string(),
  liberadoEm: z.string(),
  horasAposMedicacao: campoDeHorasAposMedicacao,
  urgente: z.boolean(),
  observacoes: z.string().trim().max(2000),
});

type Formulario = z.infer<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  exame?: ExameResponse;
}

/**
 * ⚠️ **Paciente e exame do catálogo só na criação.** Trocá-los depois mudaria
 * o significado dos resultados já lançados — os analitos vêm do catálogo, e a
 * interpretação foi calculada contra a espécie daquele paciente. Para corrigir,
 * cancela-se o exame e abre-se outro.
 */
export function ExameFormDialogo({ aberto, aoFechar, exame }: Props) {
  const queryClient = useQueryClient();
  const criar = useExamesControllerCriar();
  const atualizar = useExamesControllerAtualizar();
  const editando = Boolean(exame);

  const { data: pacientes } = usePacientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: aberto && !editando } },
  );

  const { data: catalogo } = useCatalogoExamesConsultaControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: aberto && !editando } },
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!aberto) return;

    reset({
      pacienteId: exame?.pacienteId ?? '',
      catalogoExameId: exame?.catalogoExameId ?? '',
      status: exame?.status ?? 'SOLICITADO',
      os: exame?.os ?? '',
      coletadoEm: isoParaData(exame?.coletadoEm ?? null),
      liberadoEm: isoParaData(exame?.liberadoEm ?? null),
      horasAposMedicacao: numeroParaCampo(exame?.horasAposMedicacao),
      urgente: exame?.urgente ?? false,
      observacoes: exame?.observacoes ?? '',
    });
  }, [aberto, exame, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    // Campo em branco vira `null`, não string vazia: na edição é o que apaga.
    const opcional = (v: string) => (v.trim() ? v.trim() : null);
    const data = (v: string) => (v ? dataParaISO(v) : null);

    const comuns = {
      status: dados.status as ExameResponseStatus,
      os: opcional(dados.os),
      coletadoEm: data(dados.coletadoEm),
      liberadoEm: data(dados.liberadoEm),
      horasAposMedicacao: horasParaEnvio(dados.horasAposMedicacao),
      urgente: dados.urgente,
      observacoes: opcional(dados.observacoes),
    };

    try {
      if (exame) {
        await atualizar.mutateAsync({ id: exame.id, data: comuns });
      } else {
        await criar.mutateAsync({
          data: {
            pacienteId: dados.pacienteId,
            catalogoExameId: dados.catalogoExameId,
            ...comuns,
          },
        });
      }

      await queryClient.invalidateQueries({ queryKey: getExamesControllerListarQueryKey() });
      toast.success(editando ? 'Exame atualizado.' : 'Exame cadastrado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o exame.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar exame' : 'Novo exame'}</DialogTitle>
          <DialogDescription>
            {editando
              ? 'Paciente e exame não mudam depois de criados — os resultados dependem deles.'
              : 'Os resultados são lançados depois, pela ação "Resultados" na lista.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          {editando ? (
            <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm space-y-1">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Paciente</span>
                <span className="font-medium text-foreground">{exame?.pacienteNome}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Exame</span>
                <span className="font-medium text-foreground">{exame?.exameNome}</span>
              </div>
            </div>
          ) : (
            <>
              <Campo id="pacienteId" rotulo="Paciente" obrigatorio erro={errors.pacienteId?.message}>
                <Controller
                  name="pacienteId"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="pacienteId"
                        className={CLASSE_CONTROLE}
                        aria-label="Paciente"
                      >
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {pacientes?.itens.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Campo>

              <Campo
                id="catalogoExameId"
                rotulo="Exame"
                obrigatorio
                erro={errors.catalogoExameId?.message}
                dica="Define os analitos que serão lançados."
              >
                <Controller
                  name="catalogoExameId"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="catalogoExameId"
                        className={CLASSE_CONTROLE}
                        aria-label="Exame"
                      >
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {catalogo?.itens.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Campo>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Campo id="status" rotulo="Status" erro={errors.status?.message}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="status" className={CLASSE_CONTROLE} aria-label="Status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {ROTULO_STATUS[s] ?? s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>

            <Campo id="os" rotulo="OS" dica="Ordem de serviço do laboratório." erro={errors.os?.message}>
              <Input id="os" className={CLASSE_CONTROLE} {...register('os')} />
            </Campo>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Campo id="coletadoEm" rotulo="Coleta" erro={errors.coletadoEm?.message}>
              <Input id="coletadoEm" type="date" className={CLASSE_CONTROLE} {...register('coletadoEm')} />
            </Campo>

            <Campo id="liberadoEm" rotulo="Liberação" erro={errors.liberadoEm?.message}>
              <Input id="liberadoEm" type="date" className={CLASSE_CONTROLE} {...register('liberadoEm')} />
            </Campo>
          </div>

          {/* Perto da coleta porque é dado DELA: o intervalo entre a última
              dose e a agulha. ⚠️ O rótulo carrega "informado pelo tutor" na
              dica, e a mesma ressalva volta no laudo — o número é relato, e
              vira critério de recorte da Análise Comparativa. */}
          <Campo
            id="horasAposMedicacao"
            rotulo="Horas após a medicação"
            dica={DICA_HORAS_APOS_MEDICACAO}
            erro={errors.horasAposMedicacao?.message}
            className="sm:max-w-[50%]"
          >
            <Input
              id="horasAposMedicacao"
              inputMode="decimal"
              placeholder="Ex.: 4,5"
              className={CLASSE_CONTROLE}
              {...register('horasAposMedicacao')}
            />
          </Campo>

          <Controller
            name="urgente"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Switch id="urgente" checked={field.value} onCheckedChange={field.onChange} />
                <label htmlFor="urgente" className="text-sm font-medium text-foreground">
                  Urgente
                </label>
              </div>
            )}
          />

          <Campo id="observacoes" rotulo="Observações" erro={errors.observacoes?.message}>
            <Textarea id="observacoes" rows={3} className={CLASSE_CONTROLE} {...register('observacoes')} />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
